<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  filters: { type: Object, required: true },
  loading: { type: Boolean, default: false },
  options: { type: Array, default: () => [] },
});

const emit = defineEmits(['apply']);
const { t } = useI18n();
const input = ref('');
const datalistId = 'sales-address-options';

const displayValue = filters => {
  const street = String(filters?.address_street || '').trim();
  const number = String(filters?.address_number || '').trim();
  return [street, number].filter(Boolean).join(', ');
};

watch(
  () => props.filters,
  value => {
    input.value = displayValue(value);
  },
  { deep: true, immediate: true }
);

const select = () => {
  const value = input.value.trim();
  const option = props.options.find(item => item.label === value);
  emit('apply', {
    address_street: option?.street || value,
    address_number: option?.number || '',
  });
};

const clear = () => {
  input.value = '';
  emit('apply', { address_street: '', address_number: '' });
};
</script>

<template>
  <div
    data-testid="sales-address-filter"
    class="relative h-14 w-[35vw] min-w-0 max-w-[688px] rounded-[12px] border-2 border-lais-border bg-white"
  >
    <label class="sr-only" :for="datalistId">
      {{ t('SALES.ADDRESS_FILTER.LABEL') }}
    </label>
    <span
      class="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-lais-primary"
      :class="
        loading ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-search'
      "
      aria-hidden="true"
    />
    <input
      :id="datalistId"
      v-model="input"
      :list="`${datalistId}-list`"
      type="search"
      autocomplete="off"
      :placeholder="t('SALES.ADDRESS_FILTER.PLACEHOLDER')"
      :disabled="loading"
      class="h-full w-full rounded-[12px] !border-0 !bg-transparent px-10 pr-24 text-base text-lais-text placeholder:text-lais-text-muted focus:outline-none"
      @change="select"
    />
    <datalist :id="`${datalistId}-list`">
      <option
        v-for="option in options"
        :key="option.id"
        :value="option.label"
      />
    </datalist>
    <button
      v-if="input"
      type="button"
      class="absolute right-2 top-1/2 -translate-y-1/2 rounded-lais px-2 py-1 text-xs font-semibold text-lais-primary hover:bg-lais-primary-50"
      @click="clear"
    >
      {{ t('SALES.ADDRESS_FILTER.CLEAR') }}
    </button>
  </div>
</template>
